/**
 * DebugSection — generic collapsible panel section.
 *
 * Renders a sticky-top header (title + optional count badge + chevron) and
 * lazily renders body children only when expanded. Persistence of the
 * collapsed state lives in `useSectionCollapsed` so each section's open/close
 * survives reload.
 */
import { ChevronDown, ChevronRight } from '@retail-ops/panel-shell/fi';
import { type ReactNode } from 'react';
import { useSectionCollapsed } from './hooks/useDebugPanelExpanded';

export interface DebugSectionProps {
  id: string;
  title: string;
  count?: number;
  defaultCollapsed?: boolean;
  /** Match-highlight: when search is active, the matched key/path. */
  searchTerm?: string;
  children: ReactNode;
  /** Optional inline action (e.g., "Clear" button on the errors section). */
  rightAction?: ReactNode;
}

export function DebugSection(props: DebugSectionProps): JSX.Element {
  // Default-collapsed (Bill 2026-04-26): the panel was unreadable when all
  // 12 sections expanded at once. User clicks chevron / row to expand.
  const { id, title, count, defaultCollapsed = true, children, rightAction } = props;
  const { collapsed, toggle } = useSectionCollapsed(id, defaultCollapsed);

  return (
    <section className={`pcb-debug-section ${collapsed ? 'is-collapsed' : ''}`}>
      {/* Whole header row is the click target — bigger, more reliable hit
         area than just the chevron button. The right-side action (e.g.,
         Clear) stops propagation so its clicks don't toggle. */}
      <header
        className="pcb-debug-section__header"
        onClick={toggle}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed ? 'true' : 'false'}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span className="pcb-debug-section__toggle">
          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          <span className="pcb-debug-section__title">{title}</span>
          {typeof count === 'number' && (
            <span className="pcb-debug-section__count">{count}</span>
          )}
        </span>
        {rightAction && (
          <div
            className="pcb-debug-section__action"
            onClick={(e) => e.stopPropagation()}
          >
            {rightAction}
          </div>
        )}
      </header>
      {!collapsed && (
        <div className="pcb-debug-section__body">
          {children}
        </div>
      )}
    </section>
  );
}

/**
 * Helper for rendering a JSON-ish value as a syntax-light pre block. Filters
 * by `searchTerm` when present; rows whose JSON.stringify doesn't match are
 * dimmed (not removed) so the panel still reads like a complete diagnostic.
 */
export function DebugJson(props: {
  value: unknown;
  searchTerm?: string;
  emptyText?: string;
}): JSX.Element {
  const { value, searchTerm, emptyText = '— no data —' } = props;
  if (value === undefined || value === null) {
    return <div className="pcb-debug-empty">{emptyText}</div>;
  }
  let str: string;
  try {
    str = JSON.stringify(value, (_k, v) => {
      if (v instanceof Date) return v.toISOString();
      return v as unknown;
    }, 2);
  } catch { str = String(value); }
  if (!str || str === '{}' || str === '[]') {
    return <div className="pcb-debug-empty">{emptyText}</div>;
  }
  if (searchTerm && searchTerm.trim()) {
    const term = searchTerm.toLowerCase();
    if (!str.toLowerCase().includes(term)) {
      return <pre className="pcb-debug-json is-dimmed">{str}</pre>;
    }
  }
  return <pre className="pcb-debug-json">{str}</pre>;
}

/**
 * Compact two-column key/value list. Use this for high-density blocks
 * (identity / environment / config counts) where a JSON dump feels heavy.
 */
export function DebugKv(props: {
  rows: ReadonlyArray<{ k: string; v: unknown }>;
  searchTerm?: string;
}): JSX.Element {
  const { rows, searchTerm } = props;
  const term = (searchTerm ?? '').trim().toLowerCase();
  return (
    <dl className="pcb-debug-kv">
      {rows.map(({ k, v }) => {
        const valueStr = formatScalar(v);
        const matched = term
          ? k.toLowerCase().includes(term) || valueStr.toLowerCase().includes(term)
          : true;
        return (
          <div className={`pcb-debug-kv__row ${matched ? '' : 'is-dimmed'}`} key={k}>
            <dt className="pcb-debug-kv__k">{k}</dt>
            <dd className="pcb-debug-kv__v">{valueStr}</dd>
          </div>
        );
      })}
    </dl>
  );
}

function formatScalar(v: unknown): string {
  if (v === null || v === undefined) return String(v);
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `[${v.length} item${v.length === 1 ? '' : 's'}]`;
  if (typeof v === 'object') return '{…}';
  return String(v);
}
